/**
 * ``<RequiresModule code="biometric_attendance">`` — route gate that
 * redirects to ``/`` when the tenant doesn't have the module enabled.
 *
 * Module gating is a navigation concern, not an error case
 * (``BIOMETRICS_SPEC.md`` §2.4) — the affected nav entry is hidden
 * from the sidebar entirely, and this gate is the belt to that
 * suspenders so a deep-linked URL or a mid-session toggle can't
 * leave a user staring at a 403 page.
 */
import { Navigate } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { ModuleCode, useMe, type ModuleCodeValue } from "@/lib/me";

interface RequiresModuleProps {
  code: ModuleCodeValue | string;
  /** Where to send disabled-module visitors. Defaults to the dashboard. */
  fallbackTo?: string;
  children: React.ReactNode;
}

export function RequiresModule({
  code,
  fallbackTo = "/",
  children,
}: RequiresModuleProps) {
  const { data, isLoading } = useMe();

  // While bootstrap is in flight, render a neutral placeholder rather
  // than flashing the content (or redirecting prematurely).
  if (isLoading || !data) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data.enabled_modules.includes(code)) {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
}

/**
 * ``<RequiresAiAssistant>`` — route gate for the AI Assistant. On top of the
 * copilot module entitlement it checks the PER-USER AI capability
 * (``can_use_ai_assistant``, i.e. the "AI User" role) — AI is a function
 * assigned as a role, so a tenant may have the module while individual users
 * stay switched off. Mirrors the backend ``RequiresAIRole`` gate.
 */
export function RequiresAiAssistant({
  fallbackTo = "/me",
  children,
}: Omit<RequiresModuleProps, "code">) {
  const { data, isLoading } = useMe();

  if (isLoading || !data) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const allowed =
    data.enabled_modules.includes(ModuleCode.COPILOT) &&
    Boolean(data.can_use_ai_assistant);
  if (!allowed) {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
}
